import {
  Button,
  Css,
  FieldGroup,
  FormLines,
  MultiSelectField,
  SelectField,
  TestIds,
  TextField,
  useTestIds,
} from "@homebound/beam";
import { Observer } from "mobx-react";
import { useCallback, useMemo, useState } from "react";
import { NextStepButton } from "src/components/stepper/useStepperWizard/NextStepButton";
import { stripEmptyString } from "src/utils";
import {
  NewDesignPackage_GlobalOptionGroupFragment,
  NewDesignPackage_GlobalOptionTypeFragment,
} from "./NewDesignPackage.generated";
import { NewDesignPackageWizardPage } from "./NewDesignPackageAtoms";
import { DesignPackageOptionFormInput, useNewDesignPackageContext } from "./NewDesignPackageContext";

export function NewDesignPackageOptions() {
  const { formState, save, globalOptionGroups } = useNewDesignPackageContext();
  const tid = useTestIds({}, "designPackageOptions");

  const filteredGlobalOptionGroups = useMemo(
    () => globalOptionGroups.sortBy((gog) => gog.type.order).filter((gog) => gog.globalOptions.nonEmpty),
    [globalOptionGroups],
  );

  const isElevation = useMemo(
    () => filteredGlobalOptionGroups.some((gog) => gog.type.isElevation && gog.forDesignExterior),
    [filteredGlobalOptionGroups],
  );

  return (
    <NewDesignPackageWizardPage>
      <div css={Css.xl2.$}>Package Options</div>
      <div css={Css.md.$}>
        Package Options are the options our buyers select to let us know what style of home they'd like and how much
        they want to spend. Please select any you'd like to be able to utilize in this Design Package.
      </div>
      {globalOptionGroups.isEmpty && <div css={Css.xl.$}>Loading...</div>}
      {filteredGlobalOptionGroups.nonEmpty && (
        <div css={Css.w100.$} {...tid.pairingTitle}>
          <div css={Css.xl2.mb2.$}>{filteredGlobalOptionGroups.map((gog) => gog.name).join(" + ")} Pairing</div>
        </div>
      )}
      <FormLines width="full">
        <Observer>
          {() => (
            <>
              {formState.options.value.map((rpo, index) => (
                <OptionSelector
                  testId={tid}
                  isElevation={isElevation}
                  key={`${rpo.id}-${index}`}
                  index={index}
                  rpo={rpo}
                  filteredGlobalOptionGroups={filteredGlobalOptionGroups}
                  save={(option) => {
                    if (option.id) {
                      const optionIndex = formState.options.value.findIndex((o) => o.id === option?.id);
                      formState.options.value[optionIndex] = option;
                    } else {
                      formState.options.value[index] = option;
                    }
                    formState.commitChanges();
                  }}
                  remove={() => {
                    formState.options.value = formState.options.value.filter((_, i) => i !== index);
                    formState.commitChanges();
                  }}
                />
              ))}
              <div css={Css.mt4.$}>
                <Button
                  key="add-another-pairing"
                  label="Add Another Pairing"
                  {...tid.addAnotherPairing}
                  variant="primary"
                  disabled={isElevation}
                  onClick={() => {
                    // push a new option with an undefined id to add a new row to the form
                    formState.options.value = [
                      ...formState.options.value,
                      { id: undefined, name: "", globalOptionGroupId: undefined, globalOptionId: undefined },
                    ];
                    formState.commitChanges();
                  }}
                />
              </div>
            </>
          )}
        </Observer>
      </FormLines>
      <NextStepButton label="Save Design Package" onClick={save} exitButton />
    </NewDesignPackageWizardPage>
  );
}

type OptionSelectorProps = {
  rpo: DesignPackageOptionFormInput;
  testId: TestIds;
  filteredGlobalOptionGroups: NewDesignPackage_GlobalOptionGroupFragment[];
  save: (option: DesignPackageOptionFormInput) => void;
  remove: () => void;
  isElevation: boolean;
  index: number;
};
function OptionSelector(props: OptionSelectorProps) {
  const { planPackageOptions, globalOptionTypes } = useNewDesignPackageContext();
  const { rpo, filteredGlobalOptionGroups, save, remove, testId, isElevation, index } = props;
  const globalOptionGroupMappers = globalOptionGroupMapper(filteredGlobalOptionGroups, globalOptionTypes).sortBy(
    (g) => {
      const groupOrType = g.globalOptionGroupOrType;
      if (groupOrType.__typename === "GlobalOptionGroup") return groupOrType.type.order;
      if (groupOrType.__typename === "GlobalOptionType") return groupOrType.order;
      return 0; // Should never happen
    },
  );
  const key = `${rpo.id}-${index}`;

  // Remove non-elevation options or elevations that don't belong to a selected plan package
  const shouldShowRemoveIcon = useMemo(() => {
    if (!isElevation) return true;
    return !planPackageOptions.some((o) => o.globalOption.id === rpo.globalOptionId);
  }, [isElevation, planPackageOptions, rpo]);

  return (
    <Observer>
      {() => (
        <FieldGroup key={key} widths={[...globalOptionGroupMappers.map((_) => "300px")]}>
          {globalOptionGroupMappers
            .flatMap((group) => {
              const { globalOptionGroupOrType: groupOrType, config } = group;

              // Elevation names come from the PP and cannot be edited
              const isDisabled =
                groupOrType.__typename === "GlobalOptionGroup" &&
                groupOrType.type.isElevation &&
                config.type === "elevation";

              const allChildren = rpo.optionPrereqChildren ?? [];
              const thisGroupChildren = allChildren.filter(
                (c) => c.globalOptionGroupId === groupOrType.id || c.globalOptionTypeId === groupOrType.id,
              );
              const otherGroupChildren = allChildren.filter(
                (c) => c.globalOptionGroupId !== groupOrType.id && c.globalOptionTypeId !== groupOrType.id,
              );

              // Autofill the selected global option names
              // for the global option group being rendered
              const selected = (
                rpo.globalOptionGroupId === groupOrType.id || rpo.globalOptionTypeId === groupOrType.id
                  ? [rpo.name]
                  : thisGroupChildren.map((o) => o.name)
              ) as string[];

              const optionKey = `${key}-${groupOrType.id}-${config.type}`;
              const globalOptions =
                groupOrType.__typename === "GlobalOptionGroup"
                  ? groupOrType.globalOptions.map((go) => ({ id: go.id, name: go.name }))
                  : [];

              switch (config.component) {
                case "text":
                  return (
                    <TextField
                      key={optionKey}
                      label={groupOrType.name}
                      disabled={isDisabled}
                      value={selected.first ?? ""}
                      onChange={(selectedGlobalOptionName) => {
                        if (stripEmptyString(selectedGlobalOptionName)) {
                          save({
                            ...rpo,
                            name: selectedGlobalOptionName!,
                            ...(groupOrType.__typename === "GlobalOptionType"
                              ? { globalOptionTypeId: groupOrType.id }
                              : { globalOptionGroupId: groupOrType.id }),
                          });
                        }
                      }}
                    />
                  );
                case "select":
                  return (
                    <SelectField
                      key={optionKey}
                      label={groupOrType.name}
                      disabled={isDisabled}
                      options={globalOptions}
                      getOptionValue={(go) => go.name}
                      value={selected.first ?? ""}
                      onSelect={(selectedGlobalOptionName) => {
                        save({
                          ...rpo,
                          name: selectedGlobalOptionName ?? "",
                          ...(groupOrType.__typename === "GlobalOptionType"
                            ? { globalOptionTypeId: groupOrType.id }
                            : { globalOptionGroupId: groupOrType.id }),
                        });
                      }}
                    />
                  );
                case "multi-select":
                  const options =
                    config.defaultOptions ??
                    [
                      ...globalOptions,
                      // New schemes are added as id=undefined
                      // Add them to the options list, so we can see them in the screen
                      ...(config.canAddScheme
                        ? thisGroupChildren
                            .filter((c) => c.id === undefined)
                            .map((c) => ({ id: undefined, name: String(c.name) }))
                        : []),
                    ].uniqueByKey("name");

                  return (
                    <div key={optionKey} css={Css.df.fdc.$}>
                      <MultiSelectField<{ id: string | undefined; name: string }, string | undefined>
                        label={groupOrType.name}
                        options={options}
                        disabled={isDisabled}
                        values={selected}
                        getOptionValue={(go) => go.name}
                        onSelect={(selectedGlobalOptionNames) => {
                          save({
                            ...rpo,
                            // Save the children keeping the ones from other groups (i.e. saving it for exterior scheme and keep the spec level)
                            optionPrereqChildren: [
                              ...selectedGlobalOptionNames.compact().map((name) => ({
                                id: thisGroupChildren.find((o) => o.name === name)?.id,
                                name: name,
                                ...(groupOrType.__typename === "GlobalOptionType"
                                  ? { globalOptionTypeId: groupOrType.id }
                                  : { globalOptionGroupId: groupOrType.id }),
                              })),
                              ...otherGroupChildren,
                            ],
                          });
                        }}
                      />
                      {config.canAddScheme && (
                        <AddSchemeForm
                          rpoId={String(rpo.id)}
                          disabled={isDisabled}
                          onAdd={(schemeName) => {
                            if (stripEmptyString(schemeName)) {
                              save({
                                ...rpo,
                                // Add the new scheme to the children list
                                optionPrereqChildren: [
                                  ...allChildren,
                                  {
                                    id: undefined,
                                    name: schemeName,
                                    ...(groupOrType.__typename === "GlobalOptionType"
                                      ? { globalOptionTypeId: groupOrType.id }
                                      : { globalOptionGroupId: groupOrType.id }),
                                  },
                                ],
                              });
                            }
                          }}
                        />
                      )}
                    </div>
                  );
                default:
                  fail(`Invalid component: ${config.component} for ${groupOrType.name}`);
              }
            })
            .concat([
              <div key={`remove-container-${key}`}>
                {shouldShowRemoveIcon && (
                  <div css={Css.df.jcc.aic.w1.if(isElevation).mt3.else.pt3.$}>
                    <Button label="" {...testId.removePairing} variant="textSecondary" icon="x" onClick={remove} />
                  </div>
                )}
              </div>,
            ])}
        </FieldGroup>
      )}
    </Observer>
  );
}

/**
 * Regarding the Design Package type (Exterior or Interior), the global options are mapped to the following:
 *
 * Interior:
 * - Interior Scheme (text field) -> Spec Level (multi-select field)
 *
 * Exterior:
 * - Elevation (select field) -> Spec Level (multi-select field) -> Exterior Scheme (multi-select field + button to add a new scheme)
 *
 */
type GlobalOptionGroupMapper = {
  globalOptionGroupOrType: NewDesignPackage_GlobalOptionGroupFragment | NewDesignPackage_GlobalOptionTypeFragment;
  config: {
    type: "interior_scheme" | "spec_level" | "elevation" | "exterior_scheme";
    component: "text" | "select" | "multi-select";
    // Button to add a new scheme, used for Exterior Scheme
    canAddScheme?: boolean;
    // Static options for the select field, used for Spec Level
    defaultOptions?: { id: string | undefined; name: string }[];
  };
};
function globalOptionGroupMapper(
  globalOptionGroups: NewDesignPackage_GlobalOptionGroupFragment[],
  globalOptionTypes: NewDesignPackage_GlobalOptionTypeFragment[],
): GlobalOptionGroupMapper[] {
  const gogConfigs = globalOptionGroups
    .filter((gog) => gog.forDesignExterior || gog.forDesignInterior)
    .map((gog) => {
      let configType: GlobalOptionGroupMapper["config"]["type"] | undefined;
      if (gog.type.isInteriorScheme) configType = "interior_scheme";
      else if (gog.type.isExteriorScheme) configType = "exterior_scheme";
      else if (gog.type.isElevation) configType = "elevation";
      else if (gog.type.isSpecLevel) configType = "spec_level";
      if (!configType) return undefined;
      const configs = gog.forDesignInterior ? interiorConfigs : exteriorConfigs;
      const config = configs.find((o) => o.type === configType)!;
      return { config, globalOptionGroupOrType: gog };
    });
  const gotConfigs = globalOptionTypes.map((got) => {
    let config: GlobalOptionGroupMapper["config"] | undefined;
    if (got.isInteriorScheme) config = interiorSchemeConfig;
    else if (got.isExteriorScheme) config = exteriorSchemeConfig;
    return config ? { config, globalOptionGroupOrType: got } : undefined;
  });
  return [...gogConfigs, ...gotConfigs].compact().uniqueBy((g) => g.config.type);
}

const interiorSchemeConfig = { type: "interior_scheme", component: "text" } as const;
const exteriorSchemeConfig = { type: "exterior_scheme", component: "multi-select", canAddScheme: true } as const;
const interiorConfigs: GlobalOptionGroupMapper["config"][] = [
  interiorSchemeConfig,
  { type: "spec_level", component: "multi-select" },
];
const exteriorConfigs: GlobalOptionGroupMapper["config"][] = [
  { type: "elevation", component: "select" },
  {
    type: "spec_level",
    component: "multi-select",
    defaultOptions: [
      { id: undefined, name: "Essential" },
      { id: undefined, name: "Deluxe" },
      { id: undefined, name: "Premium" },
    ],
  },
  exteriorSchemeConfig,
];

type AddSchemeFormProps = {
  disabled: boolean;
  onAdd: (schemeName: string) => void;
  rpoId: string;
};
// TODO: Replace the wizard stepper for FormPageLayout then use a modal here.
// Ideally we would use a modal to add the new scheme, but as this is a wizard stepper which is
// rendered in a modal, we can't open another one.
function AddSchemeForm({ onAdd, rpoId, disabled }: AddSchemeFormProps) {
  const [schemeName, setSchemeName] = useState("");
  const [showAddScheme, setShowAddScheme] = useState(false);

  const close = useCallback(() => {
    setShowAddScheme(false);
    setSchemeName("");
  }, []);

  return (
    <div css={Css.$}>
      <>
        {!showAddScheme && (
          <Button
            label="Add Scheme"
            variant="tertiary"
            onClick={() => setShowAddScheme(!showAddScheme)}
            disabled={disabled}
          />
        )}
      </>
      {showAddScheme && (
        <div css={Css.pt1.pr3.bgGray100.$}>
          <div key={`add-scheme-${rpoId}`}>
            <TextField
              label=""
              data-testid="schemeName"
              labelStyle="hidden"
              placeholder="Scheme Name"
              value={schemeName}
              onChange={(value) => setSchemeName(value ?? "")}
            />
            <div css={Css.df.gap2.mt1.$}>
              <Button variant="quaternary" label="Cancel" onClick={close} />
              <Button
                label="Add"
                variant="primary"
                onClick={() => {
                  onAdd(schemeName);
                  close();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
